/**
 * SkerpaFloatingBar — Multi-mode floating Skerpa panel.
 *
 * Modes:
 *   pill      — collapsed bar at bottom-center (default), draggable
 *   open      — expanded floating panel, draggable via grip
 *   minimized — small icon bubble, like chatbot icons, draggable
 *   fullscreen — covers entire viewport
 *   (sidebar) — hands off to HermesChatPanel via store
 *
 * Same agent state as HermesChatPanel: shared useHermesChat store.
 */

import { useState, useRef, useEffect, useCallback, forwardRef } from 'react'
import {
  Sparkles, ChevronDown, Trash2, PanelRight,
  Maximize2, Minimize2, Minus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHermesChat } from '@/hooks/useHermesChat'
import { useSkerpaStore } from '@/stores/skerpaStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { useProactiveGuide } from '@/hooks/useProactiveGuide'
import { SKERPA_MODE_LABELS, SKERPA_MODE_DESCRIPTIONS } from '@ispot/shared'
import type { SkerpaMode, GuideDomain, JourneyPhase } from '@ispot/shared'
import { SkerpaChatTab } from './SkerpaChatTab'
import { SkerpaSuggestionsTab } from './SkerpaSuggestionsTab'
import { SkerpaActivityTab } from './SkerpaActivityTab'
import './SkerpaFloatingBar.css'

// ── Types ──────────────────────────────────────────────────────────

type DisplayMode = 'pill' | 'open' | 'minimized' | 'fullscreen'
type TabId = 'chat' | 'suggestions' | 'activity'

interface DragPos { x: number; y: number }

interface Props {
  isExpanded: boolean
  onExpandedChange: (v: boolean) => void
  onViewChange?: (view: string) => void
}

// ── Constants ──────────────────────────────────────────────────────

const SKERPA_MODES: SkerpaMode[] = ['standard', 'plus', 'premium']
const MODE_SHORT: Record<SkerpaMode, string> = { standard: 'STD', plus: 'PLUS', premium: 'PRO' }

const DOMAIN_COLOR: Record<GuideDomain, string> = {
  market:   'var(--color-market,   #2BBCC7)',
  business: 'var(--color-business, #3196E2)',
  solution: 'var(--color-solution, #FFC05F)',
  general:  'var(--color-text-muted, #8a8580)',
}

// ── Component ──────────────────────────────────────────────────────

export function SkerpaFloatingBar({ isExpanded, onExpandedChange, onViewChange }: Props) {
  const [mode, setMode] = useState<DisplayMode>(isExpanded ? 'open' : 'pill')
  const [activeTab, setActiveTab] = useState<TabId>('chat')
  const [showModeMenu, setShowModeMenu] = useState(false)
  const [dragPos, setDragPos] = useState<DragPos | null>(null)

  const { messages, clearHistory, sendMessage } = useHermesChat()
  const skerpaMode = useSkerpaStore(s => s.skerpaMode)
  const setSkerpaMode = useSkerpaStore(s => s.setSkerpaMode)
  const setSkerpaLayout = useSkerpaStore(s => s.setSkerpaLayout)
  const currentPhase = useJourneyStore(s => s.getCurrentPhase())
  const { cards: guideCards } = useProactiveGuide()

  const bubbleRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const modeMenuRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const livePos = useRef<DragPos | null>(null)

  const activityCount = messages.length

  const modeColor =
    skerpaMode === 'premium'
      ? { bg: '#fbe7a1', fg: '#8a5a00' }
      : skerpaMode === 'plus'
        ? { bg: 'rgb(219 234 254)', fg: 'rgb(29 78 216)' }
        : { bg: 'var(--color-warm-200)', fg: 'var(--color-text-secondary)' }

  // Sync with parent (header toggle button)
  useEffect(() => {
    if (isExpanded && mode === 'pill') setMode('open')
    if (!isExpanded && mode === 'open') setMode('pill')
  }, [isExpanded]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Bubble drag (only the minimized icon is draggable) ──────────

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragStateRef.current || !bubbleRef.current) return
    const el = bubbleRef.current
    const dx = e.clientX - dragStateRef.current.startX
    const dy = e.clientY - dragStateRef.current.startY
    const raw = { x: dragStateRef.current.origX + dx, y: dragStateRef.current.origY + dy }
    const clamped = {
      x: Math.max(0, Math.min(raw.x, window.innerWidth  - el.offsetWidth)),
      y: Math.max(0, Math.min(raw.y, window.innerHeight - el.offsetHeight)),
    }
    el.style.left      = `${clamped.x}px`
    el.style.top       = `${clamped.y}px`
    el.style.bottom    = 'auto'
    el.style.right     = 'auto'
    livePos.current = clamped
  }, [])

  const onPointerUp = useCallback(() => {
    if (livePos.current) setDragPos({ ...livePos.current })
    dragStateRef.current = null
    document.removeEventListener('pointermove', onPointerMove)
  }, [onPointerMove])

  const startBubbleDrag = (e: React.PointerEvent) => {
    e.preventDefault()
    const rect = bubbleRef.current!.getBoundingClientRect()
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top }
    const el = bubbleRef.current!
    el.style.left   = `${rect.left}px`
    el.style.top    = `${rect.top}px`
    el.style.bottom = 'auto'
    el.style.right  = 'auto'
    livePos.current = { x: rect.left, y: rect.top }
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp, { once: true })
  }

  // ── Mode actions ─────────────────────────────────────────────────

  const openPanel = () => { setMode('open'); onExpandedChange(true) }
  const closeToP = () => { setMode('pill'); onExpandedChange(false) }
  const minimize  = () => { setMode('minimized'); onExpandedChange(false) }
  const fullscreen = () => setMode('fullscreen')
  const exitFull  = () => setMode('open')

  const switchToSidebar = () => {
    setSkerpaLayout('sidebar')
    setMode('pill')
    onExpandedChange(false)
  }

  // ── Outside-click (open mode only) ───────────────────────────────

  useEffect(() => {
    if (mode !== 'open') return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) closeToP()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mode-menu outside-click
  useEffect(() => {
    if (!showModeMenu) return
    const handler = (e: MouseEvent) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(e.target as Node)) setShowModeMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showModeMenu])

  // ── Minimized bubble (draggable icon) ────────────────────────────

  if (mode === 'minimized') {
    return (
      <button
        ref={bubbleRef}
        className="sfb-bubble"
        onClick={openPanel}
        onPointerDown={startBubbleDrag}
        style={dragPos ? { left: dragPos.x, top: dragPos.y, bottom: 'auto', right: 'auto' } : undefined}
        aria-label="Open Skerpa"
        title="Open Skerpa"
      >
        <Sparkles size={20} strokeWidth={1.75} />
        {messages.length > 0 && (
          <span className="sfb-bubble-badge">{messages.length > 9 ? '9+' : messages.length}</span>
        )}
      </button>
    )
  }

  // ── Fullscreen ───────────────────────────────────────────────────

  if (mode === 'fullscreen') {
    return (
      <div className="sfb-fullscreen" ref={panelRef}>
        <div className="sfb-header">
          <div className="sfb-header-left">
            <Sparkles size={14} style={{ color: 'var(--color-solution-600, #e8a756)' }} />
            <span className="sfb-header-title">Skerpa</span>
            <ModeSelector ref={modeMenuRef} skerpaMode={skerpaMode} setSkerpaMode={setSkerpaMode}
              showMenu={showModeMenu} setShowMenu={setShowModeMenu} modeColor={modeColor} />
          </div>
          <div className="sfb-header-right">
            {activeTab === 'chat' && messages.length > 0 && (
              <button className="sfb-icon-btn" onClick={clearHistory} title="Clear conversation"><Trash2 size={13} strokeWidth={1.75} /></button>
            )}
            <button className="sfb-icon-btn" onClick={switchToSidebar} title="Pin to right"><PanelRight size={14} strokeWidth={1.75} /></button>
            <button className="sfb-icon-btn" onClick={exitFull} title="Exit full screen"><Minimize2 size={14} strokeWidth={1.75} /></button>
          </div>
        </div>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} activityCount={activityCount} />
        <PhaseStrip phase={currentPhase} show={activeTab === 'chat'} />
        <div className="sfb-content">
          {activeTab === 'chat'        && <SkerpaChatTab isOpen onViewChange={onViewChange} />}
          {activeTab === 'suggestions' && <SkerpaSuggestionsTab suggestions={[]} />}
          {activeTab === 'activity'    && <SkerpaActivityTab />}
        </div>
      </div>
    )
  }

  // ── Pill + Open (always fixed bottom-center) ─────────────────────

  return (
    <div className="sfb-root">
      <div
        ref={panelRef}
        className={cn('sfb-panel', mode === 'open' && 'sfb-panel--open')}
      >
        {/* Expanded header */}
        {mode === 'open' && (
          <div className="sfb-header">
            <div className="sfb-header-left">
              <Sparkles size={14} style={{ color: 'var(--color-solution-600, #e8a756)' }} />
              <span className="sfb-header-title">Skerpa</span>
              <ModeSelector ref={modeMenuRef} skerpaMode={skerpaMode} setSkerpaMode={setSkerpaMode}
                showMenu={showModeMenu} setShowMenu={setShowModeMenu} modeColor={modeColor} />
            </div>

            <div className="sfb-header-right">
              {activeTab === 'chat' && messages.length > 0 && (
                <button className="sfb-icon-btn" onClick={clearHistory} title="Clear conversation"><Trash2 size={13} strokeWidth={1.75} /></button>
              )}
              <button className="sfb-icon-btn" onClick={minimize} title="Minimize to icon"><Minus size={14} strokeWidth={2} /></button>
              <button className="sfb-icon-btn" onClick={fullscreen} title="Expand in content area"><Maximize2 size={14} strokeWidth={1.75} /></button>
              <button className="sfb-icon-btn" onClick={switchToSidebar} title="Pin to right sidebar"><PanelRight size={14} strokeWidth={1.75} /></button>
              <button className="sfb-icon-btn" onClick={closeToP} title="Collapse"><ChevronDown size={15} strokeWidth={1.75} /></button>
            </div>
          </div>
        )}

        {/* Tabs */}
        {mode === 'open' && (
          <Tabs activeTab={activeTab} setActiveTab={setActiveTab} activityCount={activityCount} />
        )}

        {/* Phase strip */}
        <PhaseStrip phase={currentPhase} show={mode === 'open' && activeTab === 'chat'} />

        {/* Tab content */}
        {mode === 'open' && (
          <div className="sfb-content">
            {activeTab === 'chat'        && <SkerpaChatTab isOpen onViewChange={onViewChange} />}
            {activeTab === 'suggestions' && <SkerpaSuggestionsTab suggestions={[]} />}
            {activeTab === 'activity'    && <SkerpaActivityTab />}
          </div>
        )}

        {/* Collapsed: context chips */}
        {mode === 'pill' && guideCards.length > 0 && (
          <div className="sfb-quick-actions">
            {guideCards.map(card => (
              <button
                key={card.id}
                className="sfb-quick-chip"
                onClick={() => { sendMessage(card.prompt); openPanel() }}
                title={card.description}
                style={{ '--chip-color': DOMAIN_COLOR[card.domain] } as React.CSSProperties}
              >
                <span className="sfb-chip-dot" />
                {card.title}
              </button>
            ))}
          </div>
        )}

        {/* Collapsed: trigger row */}
        {mode === 'pill' && (
          <div className="sfb-pill-row">
            <button className="sfb-collapsed-bar" onClick={openPanel} aria-label="Open Skerpa">
              <Sparkles size={13} style={{ color: 'var(--color-solution-600, #e8a756)', flexShrink: 0 }} />
              <span className="sfb-collapsed-label">Ask Skerpa…</span>
              {messages.length > 0 && (
                <span className="sfb-history-badge">{messages.length}</span>
              )}
            </button>
            <button className="sfb-pill-minimize" onClick={minimize} title="Minimize to icon" aria-label="Minimize">
              <Minus size={12} strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────

const ModeSelector = forwardRef<HTMLDivElement, {
  skerpaMode: SkerpaMode
  setSkerpaMode: (m: SkerpaMode) => void
  showMenu: boolean
  setShowMenu: (v: boolean) => void
  modeColor: { bg: string; fg: string }
}>(function ModeSelector({ skerpaMode, setSkerpaMode, showMenu, setShowMenu, modeColor }, ref) {
  return (
    <div className="relative" ref={ref}>
      <button
        className="sfb-mode-badge"
        style={{ backgroundColor: modeColor.bg, color: modeColor.fg }}
        onClick={() => setShowMenu(!showMenu)}
      >
        {MODE_SHORT[skerpaMode]}
      </button>
      {showMenu && (
        <div className="sfb-mode-menu">
          {SKERPA_MODES.map(m => (
            <button
              key={m}
              className={cn('sfb-mode-item', m === skerpaMode && 'sfb-mode-item--active')}
              onClick={() => { setSkerpaMode(m); setShowMenu(false) }}
            >
              <span className="sfb-mode-label">{SKERPA_MODE_LABELS[m]}</span>
              <span className="sfb-mode-desc">{SKERPA_MODE_DESCRIPTIONS[m]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
})

function Tabs({ activeTab, setActiveTab, activityCount }: {
  activeTab: TabId; setActiveTab: (t: TabId) => void; activityCount: number
}) {
  return (
    <div className="sfb-tabs">
      <SfbTab label="Chat" active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} />
      <SfbTab label="Suggestions" active={activeTab === 'suggestions'} onClick={() => setActiveTab('suggestions')} />
      <SfbTab label="Activity" count={activityCount} active={activeTab === 'activity'} onClick={() => setActiveTab('activity')} />
    </div>
  )
}

function PhaseStrip({ phase, show }: { phase: JourneyPhase | null | undefined; show: boolean }) {
  if (!show || !phase) return null
  return (
    <div className="sfb-phase-strip">
      <span className="sfb-phase-name">Phase {phase.order}: {phase.name}</span>
      {phase.description && <span className="sfb-phase-desc"> — {phase.description}</span>}
    </div>
  )
}

function SfbTab({ label, count, active, onClick }: { label: string; count?: number; active: boolean; onClick: () => void }) {
  return (
    <button className={cn('sfb-tab', active && 'sfb-tab--active')} onClick={onClick}>
      {label}
      {count !== undefined && count > 0 && <span className="sfb-tab-count">{count}</span>}
    </button>
  )
}
